import React, { useState, useEffect, useContext } from "react";
import { css } from "emotion";
import { Button, Grid, Message, Modal } from "semantic-ui-react";
import { getRequest, postRequest } from "../../../helpers/ApiHelpers";
import { withRouter } from "react-router-dom";
import { UserContext } from "../../User";
import { StateContext } from "../../State";
import Card from "semantic-ui-react/dist/commonjs/views/Card";
import { convertToFileDownload } from "../../../helpers/ConvertToFileDownload";
import { Highlight } from "../../search/Highlight";
import "reactjs-popup/dist/index.css";
import EventLogs from "./EventLogs";
import DOMPurify from "dompurify";

const paragraphStyles = css({
  color: "#000",
  fontSize: 14,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
});
const labelStyles = css({
  fontFamily: "Fakt-Bold",
  fontSize: 14,
  color: "#707898",
  marginBottom: 14,
  marginTop: 24,
});

function FinCEN({ history, match: { params }, finCENSearchResults }) {
  const { user } = useContext(UserContext);
  const { selectedState } = useContext(StateContext);

  const [state, setState] = useState({
    finCENs: [],
    noResults: false,
    initialLoaded: false,
    finCENToDelete: null,
  });

  async function getFinCENsByState() {
    const result = await getRequest("/finCEN/getFinCENsByState", {
      stateId: selectedState,
    });
    if (result.success) {
      setState((state) => ({
        ...state,
        finCENs: result.data,
        initialLoaded: true,
      }));
    }
  }

  async function deleteFinCEN(id) {
    const result = await postRequest(
      `/finCEN/deleteFinCEN?id=${id}`,
      id
    );
    if (result.success) {
      setState((state) => ({
        ...state,
        finCENs: state.finCENs.filter((i) => i.id !== id),
        finCENToDelete: null,
      }));
    }
  }

  async function download(id) {
    const result = await getRequest(
      `/finCEN/downloadFinCENFile?id=${id}`
    );
    if (result.success) {
      convertToFileDownload(result.data.base64File, result.data.name);
    }
  }

  useEffect(() => {
    if (selectedState && !finCENSearchResults) {
      getFinCENsByState();
    }
  }, [selectedState]);

  const filteredFinCENs = finCENSearchResults
    ? finCENSearchResults
    : state.finCENs;

  return (
    <React.Fragment>
      {!finCENSearchResults && (
        <Grid>
          <Grid.Row columns={2} verticalAlign="middle">
            {(user.isOwner || user.isOwnerPlus) && (
              <Grid.Column
                floated="right"
                className={css({ textAlign: "right" })}
              >
                <Button
                  icon
                  className="blueButton"
                  onClick={() => history.push(`/info/new-fincen`)}
                >
                  <i className="fal fa-plus" />
                </Button>
                <span
                  className={css({
                    fontFamily: "Fakt-Bold",
                    fontSize: 14,
                    color: "#000",
                    cursor: "pointer",
                    "&:hover": {
                      color: "#00b8d4",
                    },
                  })}
                  onClick={() => history.push(`/info/new-fincen`)}
                >
                  New FinCEN
                </span>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      )}
      <div
        className={css({
          marginBottom: finCENSearchResults ? 0 : 19.75,
          marginTop: finCENSearchResults ? 0 : 40,
        })}
      >
        {filteredFinCENs &&
          filteredFinCENs.map((i) => (
            <Card fluid style={{ padding: 28 }} key={i.id}>
              <Card.Content>
                {i.eventLogs && i.eventLogs.length > 0 && (
                  <EventLogs
                    eventLogs={i.eventLogs}
                    position={{
                      right: user.isOwner || user.isOwnerPlus ? 82 : 24,
                    }}
                  />
                )}
                {(user.isOwner || user.isOwnerPlus) && (
                  <React.Fragment>
                    <i
                      onClick={() =>
                        setState((state) => ({
                          ...state,
                          finCENToDelete: i,
                        }))
                      }
                      className="fal fa-trash"
                      style={{
                        position: "absolute",
                        right: 56,
                        cursor: "pointer",
                      }}
                    />
                    <i
                      onClick={() =>
                        history.push(`/info/edit-fincen/${i.id}`)
                      }
                      className="fal fa-pencil"
                      style={{
                        position: "absolute",
                        right: 28,
                        cursor: "pointer",
                      }}
                    />
                  </React.Fragment>
                )}
                <Card.Header content={<Highlight children={i.subject} />} />
                <Card.Description>
                  <div
                    className={css({
                      "& strong, & b": { fontFamily: "Fakt-Bold !important" },
                      "& em, & i": { fontStyle: "italic !important" },
                      "& u": { textDecoration: "underline !important" },
                      "& s": { textDecoration: "line-through !important" },
                    })}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(i.note) }}
                  />
                </Card.Description>
                {i.finCENFiles && i.finCENFiles.length > 0 && (
                  <div className={labelStyles}>Attachments</div>
                )}
                {i.finCENFiles &&
                  i.finCENFiles.map((f) => (
                    <p className={paragraphStyles} key={f.id}>
                      <a
                        onClick={() => download(f.id)}
                        className={css({
                          "&:hover": {
                            textDecoration: "underline",
                            color: "#00b8d4",
                          },
                        })}
                      >
                        <i
                          className="fal fa-paperclip"
                          style={{ color: "#00b8d4", marginRight: 8 }}
                        />
                        <Highlight children={f.fileName} />
                      </a>
                    </p>
                  ))}
              </Card.Content>
            </Card>
          ))}
        {filteredFinCENs.length < 1 && state.initialLoaded && (
          <Message size="big">No available FinCEN entries...</Message>
        )}
      </div>
      {state.finCENToDelete && (
        <Modal
          size={"tiny"}
          open={state.finCENToDelete !== null}
          onClose={() =>
            setState((state) => ({ ...state, finCENToDelete: null }))
          }
          dimmer="inverted"
        >
          <Modal.Header>Delete FinCEN</Modal.Header>
          <Modal.Content>
            <p>Are you sure you want to delete?</p>
          </Modal.Content>
          <Modal.Actions>
            <Button
              negative
              onClick={() =>
                setState((state) => ({ ...state, finCENToDelete: null }))
              }
              style={{
                backgroundColor: "#fff",
                border: "solid 1px #e5e5ea",
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              No
            </Button>
            <Button
              positive
              onClick={() => deleteFinCEN(state.finCENToDelete.id)}
              style={{ backgroundColor: "#1f2b5e" }}
            >
              Yes
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </React.Fragment>
  );
}

export default withRouter(FinCEN);
